import { useState, useEffect } from 'react';
import { exportProtocolPDF, saveProtocolVersion, listProtocolVersions, deleteProtocolVersion, generateFlowchart, publishProtocolVersion, unpublishProtocolVersion } from '../api';
import ProtocolFlowchart from './ProtocolFlowchart';

export default function ProtocolEditor({ protocol, protocolTitle = 'Research Protocol', onVersionLoad }) {
  const [sections, setSections] = useState(
    protocol?.sections?.map((s) => ({ ...s })) || []
  );
  const [title, setTitle] = useState(protocolTitle);
  const [copied, setCopied] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const [showVersionInput, setShowVersionInput] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);
  const [versionSaveMsg, setVersionSaveMsg] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadedVersionId, setLoadedVersionId] = useState(null);
  const [showFlowchart, setShowFlowchart] = useState(false);
  const [flowchartCode, setFlowchartCode] = useState('');
  const [generatingFlowchart, setGeneratingFlowchart] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [publishMsg, setPublishMsg] = useState('');
  const [faithOpen, setFaithOpen] = useState(false);

  const fetchVersions = async () => {
    setVersionsLoading(true);
    setVersionsError('');
    try {
      const res = await listProtocolVersions();
      setVersions(res.data || []);
    } catch {
      setVersionsError('Could not load version history.');
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const handleChange = (index, newContent) => {
    setSections((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], content: newContent };
      return updated;
    });
    setLoadedVersionId(null);
  };

  const handleCopy = async () => {
    const fullText = sections
      .map((s) => `## ${s.title}\n\n${s.content}`)
      .join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = fullText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await exportProtocolPDF(title, sections);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/\s+/g, '_')}_protocol.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'PDF export failed.');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!versionName.trim()) return;
    setSavingVersion(true);
    setVersionSaveMsg('');
    try {
      await saveProtocolVersion(versionName.trim(), title, sections);
      setVersionSaveMsg(`Version "${versionName.trim()}" saved.`);
      setVersionName('');
      setShowVersionInput(false);
      await fetchVersions();
      setTimeout(() => setVersionSaveMsg(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || !err.response) {
        setVersionSaveMsg('Request timed out — backend may be waking up, please try again.');
      } else {
        setVersionSaveMsg(typeof detail === 'string' ? detail : `Save failed (${err.response?.status ?? 'network'}).`);
      }
    } finally {
      setSavingVersion(false);
    }
  };

  const handleLoadVersion = (version) => {
    if (!window.confirm(`Load version "${version.name}"? This will replace your current content.`)) return;
    const newTitle = version.title || title;
    const newSections = (version.sections || []).map((s) => ({ ...s }));
    setTitle(newTitle);
    setSections(newSections);
    setLoadedVersionId(version.id);
    if (onVersionLoad) {
      onVersionLoad({ title: newTitle, sections: newSections });
    }
  };

  const handleDeleteVersion = async (id, name) => {
    if (!window.confirm(`Delete version "${name}"?`)) return;
    try {
      await deleteProtocolVersion(id);
      setVersions((prev) => prev.filter((v) => v.id !== id));
      if (loadedVersionId === id) setLoadedVersionId(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed.');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  const handleGenerateFlowchart = async () => {
    const methodsSection = sections.find(
      (s) => s.title?.toLowerCase().includes('method') || s.title?.toLowerCase().includes('step')
    );
    if (!methodsSection?.content?.trim()) {
      alert('No methods section found. Generate a protocol first.');
      return;
    }
    setGeneratingFlowchart(true);
    try {
      const res = await generateFlowchart(methodsSection.content);
      setFlowchartCode(res.data.mermaid);
      setShowFlowchart(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Flowchart generation failed.');
    } finally {
      setGeneratingFlowchart(false);
    }
  };

  const handlePublish = async (version) => {
    setPublishingId(version.id);
    setPublishMsg('');
    try {
      const res = await publishProtocolVersion(version.id);
      const slug = res.data.slug;
      setVersions((prev) => prev.map((v) =>
        v.id === version.id ? { ...v, is_public: true, public_slug: slug } : v
      ));
      setPublishMsg(`Published! Sharing link copied.`);
      const url = `${window.location.origin}/p/${slug}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      setTimeout(() => setPublishMsg(''), 4000);
    } catch (err) {
      const d = err.response?.data?.detail;
      setPublishMsg(typeof d === 'string' ? d : 'Publish failed.');
      setTimeout(() => setPublishMsg(''), 4000);
    } finally {
      setPublishingId(null);
    }
  };

  const handleUnpublish = async (version) => {
    setPublishingId(version.id);
    try {
      await unpublishProtocolVersion(version.id);
      setVersions((prev) => prev.map((v) =>
        v.id === version.id ? { ...v, is_public: false } : v
      ));
    } catch (err) {
      const d2 = err.response?.data?.detail;
      setPublishMsg(typeof d2 === 'string' ? d2 : 'Unpublish failed.');
      setTimeout(() => setPublishMsg(''), 3000);
    } finally {
      setPublishingId(null);
    }
  };

  const handleCopyShareLink = async (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    try { await navigator.clipboard.writeText(url); } catch {}
    setPublishMsg('Link copied!');
    setTimeout(() => setPublishMsg(''), 2000);
  };

  if (!protocol) return null;

  return (
    <div
      className="bg-white rounded-lg p-4"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1 text-base font-semibold text-[#171717] border-b border-transparent hover:border-[#ebebeb] focus:border-[#0a72ef] focus:outline-none px-0 py-0.5 bg-transparent"
            style={{ letterSpacing: '-0.32px' }}
            placeholder="Protocol title..."
          />
          {loadedVersionId && (
            <span className="text-xs text-[#4d4d4d] font-medium whitespace-nowrap">
              Loaded: {versions.find((v) => v.id === loadedVersionId)?.name || 'version'}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 items-center">
          {/* Share / Copy Link — always visible in toolbar */}
          {(() => {
            const lv = versions.find((v) => v.id === loadedVersionId);
            if (lv?.is_public) {
              return (
                <button
                  onClick={() => handleCopyShareLink(lv.public_slug)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#0a72ef] text-white hover:bg-[#0860cc] transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                  </svg>
                  Copy Link
                </button>
              );
            }
            return (
              <button
                onClick={() => lv && handlePublish(lv)}
                disabled={!lv || publishingId === lv?.id}
                title={!lv ? 'Load a saved version to share' : ''}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#0a72ef] text-white hover:bg-[#0860cc] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                </svg>
                {publishingId === lv?.id ? 'Sharing...' : 'Share'}
              </button>
            );
          })()}

          {showVersionInput ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveVersion();
                  if (e.key === 'Escape') setShowVersionInput(false);
                }}
                placeholder="Version name..."
                className="px-2 py-1 text-sm text-[#171717] placeholder-[#808080] bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#0a72ef] w-36"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
              <button
                onClick={handleSaveVersion}
                disabled={!versionName.trim() || savingVersion}
                className="px-2.5 py-1 text-sm font-medium rounded-md bg-[#171717] text-white hover:bg-[#2e2e2e] disabled:opacity-50 transition-colors"
              >
                {savingVersion ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowVersionInput(false)}
                className="px-2 py-1 text-sm text-[#4d4d4d] hover:text-[#171717] rounded-md transition-colors"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowVersionInput(true)}
              className="px-3 py-1.5 text-sm font-medium rounded-md text-[#4d4d4d] hover:text-[#171717] transition-colors"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              Save Version
            </button>
          )}

          <button
            onClick={handleGenerateFlowchart}
            disabled={generatingFlowchart}
            className="px-3 py-1.5 text-sm font-medium rounded-md text-[#4d4d4d] hover:text-[#171717] disabled:opacity-50 transition-colors"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            {generatingFlowchart ? 'Generating...' : 'Flowchart'}
          </button>

          <button
            onClick={handleExportPDF}
            disabled={exportingPDF}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-[#171717] text-white hover:bg-[#2e2e2e] disabled:opacity-50 transition-colors"
          >
            {exportingPDF ? 'Exporting...' : 'Export PDF'}
          </button>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 text-sm font-medium rounded-md text-[#4d4d4d] hover:text-[#171717] transition-colors"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {versionSaveMsg && (
        <p className={`text-xs font-medium mb-2 ${versionSaveMsg.includes('ailed') ? 'text-red-600' : 'text-[#4d4d4d]'}`}>
          {versionSaveMsg}
        </p>
      )}
      {publishMsg && (
        <p className={`text-xs font-medium mb-2 ${publishMsg.includes('ailed') ? 'text-red-600' : 'text-[#0a72ef]'}`}>
          {publishMsg}
        </p>
      )}

      {protocol.mock_mode && (
        <div className="mb-4 px-3 py-2 bg-amber-50 rounded text-amber-700 text-xs font-medium" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
          MOCK MODE — Sample protocol. Add OPENROUTER_API_KEY to backend/.env for real AI protocols.
        </div>
      )}

      {/* Faithfulness score */}
      {protocol.faithfulness && (
        <div className="mb-4">
          {(() => {
            const f = protocol.faithfulness;
            const pct = Math.round(f.score * 100);
            const color = pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';
            const bg = pct >= 80 ? '#f0fdf4' : pct >= 50 ? '#fffbeb' : '#fef2f2';
            const border = pct >= 80 ? 'rgba(22,163,74,0.25)' : pct >= 50 ? 'rgba(217,119,6,0.25)' : 'rgba(220,38,38,0.25)';
            const flagged = f.checks.filter((c) => c.verdict !== 'supported');
            return (
              <div className="rounded-lg overflow-hidden" style={{ boxShadow: `${border} 0px 0px 0px 1px` }}>
                <button
                  onClick={() => flagged.length > 0 && setFaithOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium transition-colors"
                  style={{ background: bg, color, cursor: flagged.length > 0 ? 'pointer' : 'default' }}
                >
                  <span className="flex items-center gap-2">
                    <span>Citation Faithfulness: {pct}%</span>
                    <span style={{ color: '#808080', fontWeight: 400 }}>
                      ({f.checked} citations checked
                      {flagged.length > 0 ? `, ${flagged.length} flagged` : ', all supported'})
                    </span>
                  </span>
                  {flagged.length > 0 && (
                    <svg
                      className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${faithOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {faithOpen && flagged.length > 0 && (
                  <div style={{ borderTop: `1px solid ${border}` }}>
                    {flagged.map((c, i) => (
                      <div
                        key={i}
                        className="px-3 py-2 text-xs"
                        style={{ borderBottom: i < flagged.length - 1 ? `1px solid ${border}` : undefined, background: '#fafafa' }}
                      >
                        <div className="flex items-start gap-2 mb-0.5">
                          <span
                            className="font-medium uppercase text-[10px] tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ background: c.verdict === 'partial' ? '#fef9c3' : '#fee2e2', color: c.verdict === 'partial' ? '#854d0e' : '#b91c1c' }}
                          >
                            {c.verdict}
                          </span>
                          <span className="font-medium text-[#171717]">{c.citation}</span>
                        </div>
                        <p className="text-[#4d4d4d] mb-1 line-clamp-2">&ldquo;{c.sentence}&rdquo;</p>
                        <p className="text-[#808080] italic">{c.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Protocol sections */}
      <div className="space-y-4">
        {sections.map((section, index) => (
          <div key={section.title}>
            <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>{section.title}</label>
            <textarea
              value={section.content}
              onChange={(e) => handleChange(index, e.target.value)}
              rows={section.title === 'Step-by-step Methods' ? 10 : 5}
              className="w-full rounded-md px-3 py-2 text-sm text-[#4d4d4d] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-y"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            />
          </div>
        ))}
      </div>

      {/* Version History */}
      <div className="mt-5 rounded-lg overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        <button
          onClick={() => {
            setHistoryOpen((o) => !o);
            if (!historyOpen) fetchVersions();
          }}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-[#fafafa] hover:bg-[#f0f0f0] transition-colors text-sm font-medium text-[#171717]"
        >
          <span className="flex items-center gap-2">
            Version History
            {versions.length > 0 && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full text-[#808080]" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                {versions.length}
              </span>
            )}
          </span>
          <svg
            className={`w-4 h-4 text-[#808080] transition-transform ${historyOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {historyOpen && (
          <div style={{ borderTop: '1px solid #ebebeb' }}>
            {/* Current unsaved */}
            <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid #ebebeb', background: '#fafafa' }}>
              <div>
                <p className="text-sm font-medium text-[#171717]">Current (unsaved)</p>
                <p className="text-xs text-[#808080] mt-0.5 line-clamp-1">{title}</p>
              </div>
              <span className="text-xs text-[#4d4d4d] font-medium">Active</span>
            </div>

            {versionsLoading && (
              <p className="px-4 py-3 text-sm text-[#808080] text-center">Loading versions...</p>
            )}
            {versionsError && (
              <p className="px-4 py-3 text-sm text-red-600">{versionsError}</p>
            )}
            {!versionsLoading && !versionsError && versions.length === 0 && (
              <p className="px-4 py-4 text-sm text-[#808080] text-center">
                No saved versions yet. Click "Save Version" to create one.
              </p>
            )}

            {versions.map((version) => (
              <div
                key={version.id}
                className="px-4 py-2.5 flex items-center justify-between hover:bg-[#fafafa] transition-colors"
                style={{
                  borderBottom: '1px solid #ebebeb',
                  background: loadedVersionId === version.id ? '#fafafa' : undefined,
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#171717] truncate">{version.name}</p>
                    {loadedVersionId === version.id && (
                      <span className="text-xs text-[#4d4d4d] font-medium whitespace-nowrap">Loaded</span>
                    )}
                  </div>
                  <p className="text-xs text-[#808080] truncate mt-0.5">
                    {version.title && <span className="mr-1">{version.title} &middot;</span>}
                    {formatDate(version.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                  <button
                    onClick={() => handleLoadVersion(version)}
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-[#171717] text-white hover:bg-[#2e2e2e] transition-colors"
                  >
                    Load
                  </button>
                  {version.is_public ? (
                    <>
                      <button
                        onClick={() => handleCopyShareLink(version.public_slug)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-[#0a72ef] text-white hover:bg-[#0860cc] transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                        </svg>
                        Copy Link
                      </button>
                      <button
                        onClick={() => handleUnpublish(version)}
                        disabled={publishingId === version.id}
                        className="px-2.5 py-1 text-xs font-medium rounded-md text-[#808080] hover:text-[#171717] transition-colors disabled:opacity-50"
                        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                      >
                        {publishingId === version.id ? '...' : 'Unpublish'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handlePublish(version)}
                      disabled={publishingId === version.id}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-[#0a72ef] text-white hover:bg-[#0860cc] transition-colors disabled:opacity-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                      {publishingId === version.id ? '...' : 'Share'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteVersion(version.id, version.name)}
                    className="px-2.5 py-1 text-xs font-medium rounded-md text-red-500 hover:text-red-700 transition-colors"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFlowchart && (
        <ProtocolFlowchart
          initialCode={flowchartCode}
          onClose={() => setShowFlowchart(false)}
        />
      )}
    </div>
  );
}
